import React, { useEffect, useState } from "react";
import * as taskService from "source/models/services/taskService";
import * as appReportService from "source/models/services/appReportService";
import { TaskRepetitionType, TaskVisibility } from "ui/Scripts/source/models/enums";
import { ISchemaParseResult, TaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { Alert, Grid } from "@mui/material";
import { ITaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { ITaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { TaskTypeDialogPreviewType, TaskTypeDialogPreviewWeb } from "./TaskTypeDialogPreviewWeb";
import { Translations } from "ui/Scripts/source/models/translations";
import { TaskTypeDialogPreviewComponentProperties } from "./TaskTypeDialogPreviewComponentProperties/TaskTypeDialogPreviewComponentProperties";
import { ITaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { ILineColumnWidth } from "../TaskTypeDialogFormDesigner/FormDesignerComponentProperties/FormDesignerComponentPropertiesRow";
import { TaskTypeDialogPreviewPrint } from "./TaskTypeDialogPreviewPrint";
import { ITaskTypeFieldPropUpdate } from "../FormDesignerTypes";
import { TaskTypePrintHtml } from "source/models/taskType/taskTypePrintHtml";
  
interface ITaskTypeDialogPreviewProps {
    appReportDesignMode?: boolean;
    previewType: TaskTypeDialogPreviewType;
    taskRepetitionType: TaskRepetitionType;
    dataSchema: string;
    headerDataRow: ITaskDataRow;
    formDataRow: ITaskDataRow;
    lineDataRow: ITaskDataRow;
    //Print preview
    companyId: string;
    code: string;
    printOrientationLandscape: boolean;
    //Properties Panel
    headerTypeCol?: ITaskTypeCol;
    formTypeCol?: ITaskTypeCol;
    lineTypeCol?: ITaskTypeCol;
    lineColumnWidths: ILineColumnWidth[];
    onHeaderItemClick: (id: string) => void;
    onFormItemClick: (id: string) => void;
    onTaskTypeHeaderPropUpdate?: (values: ITaskTypeFieldPropUpdate[]) => void;
    onTaskTypeFormPropUpdate?: (values: ITaskTypeFieldPropUpdate[]) => void;
    onUpdateHeaderDataRowCols: (taskDataRowCols: ITaskDataRowCol[]) => void;
    onUpdateFormDataRowCols: (taskDataRowCols: ITaskDataRowCol[]) => void;
    onChangeLineColumnWidth: (id: string, classes: string) => void;
}

export const TaskTypeDialogPreview = (
    props: ITaskTypeDialogPreviewProps
) => {
    const [state, setState] = useState<ISchemaParseResult>(null);

    useEffect(() => {
        setState(TaskEditTypeItem.getTaskTypeFromSchema(props.dataSchema));
    }, [props.dataSchema]);

    const handleGetPrintHtml = (obj: any): Promise<TaskTypePrintHtml> => {
        if (!props.appReportDesignMode) {
            const getPreviewPrintHtml = props.previewType === TaskTypeDialogPreviewType.Print
                ? taskService.getTaskTypePreviewPrintHtml
                : taskService.getTaskTypeRowPreviewPrintHtml;
            return getPreviewPrintHtml(obj);
        } else {
            return appReportService.getAppReportTemplatePreviewPrintHtml(obj);
        }
    };

    const handleGetPrint = (obj: any): Promise<string> => {
        if (!props.appReportDesignMode) {
            const getPreviewPrintHtml = props.previewType === TaskTypeDialogPreviewType.Print
                ? taskService.getTaskTypePreviewPrint
                : taskService.getTaskTypeRowPreviewPrint;
            return getPreviewPrintHtml(obj);
        } else {
            return appReportService.getAppReportTemplatePreviewPrint(obj);
        }
    };
    
    if (!state) return null;

    return (
        <GridRow>
            {!!props.appReportDesignMode &&
                <Grid item sm={1}></Grid>
            }
            <Grid item sm={!props.appReportDesignMode ? 9 : 10}>
                {!!state.error &&
                    <div>
                        <Alert severity="error">{state.error}</Alert>
                    </div>
                }
                {!!state.taskType &&
                    <>
                        {props.previewType === TaskTypeDialogPreviewType.Web &&
                            <TaskTypeDialogPreviewWeb
                                title={Translations.FormWeb}
                                previewType={TaskTypeDialogPreviewType.Web}
                                taskRepetitionType={props.taskRepetitionType}
                                taskType={state.taskType}
                                headerDataRow={props.headerDataRow}
                                formDataRow={props.formDataRow}
                                lineDataRow={props.lineDataRow}
                                headerTypeCol={props.headerTypeCol}
                                formTypeCol={props.formTypeCol}
                                onHeaderItemClick={props.onHeaderItemClick}
                                onFormItemClick={props.onFormItemClick}
                                onUpdateHeaderDataRowCols={props.onUpdateHeaderDataRowCols}
                                onUpdateFormDataRowCols={props.onUpdateFormDataRowCols}
                            />
                        }
                        {props.previewType === TaskTypeDialogPreviewType.Mobile &&
                            <Grid container spacing={0}>
                                <Grid item sm={4}></Grid>
                                <Grid item sm={4}>
                                    <TaskTypeDialogPreviewWeb
                                        title={Translations.FormMobile}
                                        previewType={TaskTypeDialogPreviewType.Mobile}
                                        taskRepetitionType={props.taskRepetitionType}
                                        taskType={state.taskType}
                                        headerDataRow={props.headerDataRow}
                                        formDataRow={props.formDataRow}
                                        lineDataRow={props.lineDataRow}
                                        headerTypeCol={props.headerTypeCol}
                                        formTypeCol={props.formTypeCol}
                                        onHeaderItemClick={props.onHeaderItemClick}
                                        onFormItemClick={props.onFormItemClick}
                                        onUpdateHeaderDataRowCols={props.onUpdateHeaderDataRowCols}
                                        onUpdateFormDataRowCols={props.onUpdateFormDataRowCols}
                                    />
                                </Grid>
                            </Grid>
                        }
                        {(props.previewType === TaskTypeDialogPreviewType.Print || props.previewType === TaskTypeDialogPreviewType.PrintRow) &&
                            <Grid container spacing={0}>
                                <Grid item sm={2}></Grid>
                                <Grid item sm={8}>
                                    <TaskTypeDialogPreviewPrint
                                        companyId={props.companyId}
                                        code={props.code}
                                        taskRepetitionType={props.taskRepetitionType}
                                        dataSchema={props.dataSchema}
                                        printOrientationLandscape={props.printOrientationLandscape}
                                        visibility={TaskVisibility.Public}
                                        title={props.previewType === TaskTypeDialogPreviewType.Print ? Translations.FormPrint : Translations.FormRowPrint}
                                        taskType={state.taskType}
                                        headerDataRow={props.headerDataRow}
                                        formDataRow={props.formDataRow}
                                        headerTypeCol={props.headerTypeCol}
                                        formTypeCol={!props.appReportDesignMode
                                            ? props.formTypeCol
                                            : null}
                                        onGetPrintHtml={handleGetPrintHtml}
                                        onGetPrint={handleGetPrint}
                                        onHeaderItemClick={props.onHeaderItemClick}
                                        onFormItemClick={!props.appReportDesignMode
                                            ? props.onFormItemClick
                                            : () => {}}
                                    />
                                </Grid>
                            </Grid>
                        }
                    </>
                }
            </Grid>
            {!props.appReportDesignMode &&
                <Grid item sm={3}>
                    <TaskTypeDialogPreviewComponentProperties
                        previewType={props.previewType}
                        taskRepetitionType={props.taskRepetitionType}
                        taskType={state.taskType}
                        headerTypeCol={props.headerTypeCol}
                        formTypeCol={props.formTypeCol}
                        lineTypeCol={props.lineTypeCol}
                        lineColumnWidths={props.lineColumnWidths}
                        onFormItemClick={props.onFormItemClick}
                        onHeaderItemClick={props.onHeaderItemClick}
                        onTaskTypeHeaderPropUpdate={props.onTaskTypeHeaderPropUpdate}
                        onTaskTypeFormPropUpdate={props.onTaskTypeFormPropUpdate}
                        onChangeLineColumnWidth={props.onChangeLineColumnWidth}
                    />
                </Grid>
            }
        </GridRow>
    );
};
